<template>
  <surg-add v-if="isReady" />
</template>

<script>
import { mapMutations, mapActions, mapGetters } from "vuex";
import SurgAdd from "./SurgAdd.vue";
import {
  insuranceQuestions,
  getShortCareServices,
  UNIQUE_INSURANCE_CODES
} from "../../../utilities/surgery-questions.js";
import { getSurgeryDocuments } from "../../../utilities/download-document.js";
import { parsePatientRequests } from "@/utilities/patient-requests";

export default {
  components: { SurgAdd },
  name: "SurgEdit",
  data() {
    return {
      insuranceQuestions,
      isReady: false,
      documents: [
        {
          type: "clinic-confirm-agreement",
          files: [],
          title: "הסכמה מדעת לפעולה*",
          note: `לתשומת ליבך, ע"פ הנחיות משרד הבריאות, מסמך הסכמה מדעת בתוקף עד 30 יום מיום חתימת המטופל.`
        },
        {
          type: "reference-document",
          files: [],
          title: "סיכום ייעוץ לקראת ניתוח*",
          note:
            "*לאור דרישות קופות החולים וחברות הביטוח, יש לצרף מסמך סיכום ייעוץ המפנה לניתוח."
        },
        {
          type: "ext-imaging-diagnosis",
          files: [],
          title: "פענוח בדיקות הדמייה*",
          note: `ע"פ נהלי הקופה, קוד הניתוח הנבחר מחייב העלאת פענוח בדיקת הדמייה.`
        },
        { type: "", files: [], title: "קבצים נוספים" }
      ]
    };
  },
  computed: {
    ...mapGetters("Surgery", ["getInsuranceByName"])
  },
  methods: {
    ...mapMutations("Surgery", [
      "SET_SURGERY",
      "SET_IS_EDIT_MODE",
      "SET_SURGERY_ID",
      "SET_INSURANCE_FORM",
      "SET_INSURANCE_SURGERY_TEMP",
      "SET_IS_INSURANCE_TYPE"
    ]),
    ...mapActions("Surgery", [
      "getImplants",
      "setInsuranceCode",
      "formatInitialSelectedSurgery"
    ]),
    getShortCareServices,

    /**
     * Sets isInsuranceType based on payType and fetches insurance codes if needed
     * This replicates the logic from getDataAfterPayTypeChanged action for edit mode
     */
    async initInsuranceType(payType, insuranceSurgeryTemp) {
      if (!payType) return;

      const selectedInsurance = this.getInsuranceByName(payType);
      if (!selectedInsurance) return;

      // Only process unique insurance codes that require surgery code mapping
      if (!UNIQUE_INSURANCE_CODES.includes(selectedInsurance.insurance_code)) {
        return;
      }

      this.SET_IS_INSURANCE_TYPE(true);

      // Fetch insurance codes for each surgery in temp array
      const insuranceId = parseInt(selectedInsurance.insurance_code);
      for (let i = 0; i < insuranceSurgeryTemp.length; i++) {
        await this.setInsuranceCode({ index: i, insuranceId });
      }
    },

    /**
     * Parses gender from API response
     */
    parseGender(gender) {
      if (gender === "male" || gender === "זכר") {
        return "male";
      } else if (gender === "female" || gender === "נקבה") {
        return "female";
      }
      return "";
    },

    /**
     * Parses surgery orders and builds selected_surgery and insurance_surgery arrays
     */
    parseSurgeryOrders(resOrders) {
      const selected_surgery = [];
      const insurance_surgery = [];

      if (resOrders?.status === "OK" && resOrders.data?.orders) {
        resOrders.data.orders.forEach(s => {
          if (s.tafnit_code === "9000") return;

          let insurance = s.insurance_name;
          if (
            s.tafnit_code == 1 &&
            insurance !== "כללית מושלם" &&
            insurance !== "פרטי"
          ) {
            insurance = "חברת ביטוח";
          }

          insurance_surgery.push({
            insurance_type: insurance,
            code: s.insurance_procedure,
            code_name_hospital: s.type,
            notes: s.notes
          });

          const duration = s.duration
            ? this.$moment.utc(s.duration * 1000).format("HH:mm")
            : "HH:mm";

          selected_surgery.push({
            id: s.id,
            is_primary: s.is_primary || "",
            code_name_clinical: s.icd9_procedure,
            code_name_hospital: s.type,
            duration: duration,
            default_surgery_duration: duration,
            side: s.side ?? "",
            insurance_type: insurance,
            newSpecialInsuranceCode: true,
            additionalInfo: {
              status: s.status,
              surgery_fee: s.surgery_fee,
              reference_id: s.reference_id,
              name: "",
              foreign_id: s.foreign_id,
              fee: s.fee,
              expiration_date: s.expiration_date
            }
          });
        });
      }

      return { selected_surgery, insurance_surgery };
    },

    /**
     * Parses diagnosis data from surgery
     */
    parseDiagnosis(diagnosisJson) {
      const current_diagnosis = [];
      const past_diagnosis = [];

      if (diagnosisJson) {
        const diagnosis = JSON.parse(diagnosisJson);

        if (diagnosis.current?.length) {
          diagnosis.current.forEach(d => {
            current_diagnosis.push({
              side: d.side ?? "",
              code: d.diagnosis_number,
              name: d.diagnosis_name,
              notes: d.notice
            });
          });
        }

        if (diagnosis.past?.length) {
          diagnosis.past.forEach(d => {
            past_diagnosis.push({
              side: d.side ?? "",
              diagnosis_number: d.diagnosis_number,
              diagnosis_name: d.diagnosis_name,
              notice: d.notice
            });
          });
        }
      }

      return { current_diagnosis, past_diagnosis };
    },

    /**
     * Parses sensitivity data from patient
     */
    parseSensitivity(sensitivityJson) {
      const medication_sensitivity = [];
      const material_sensitivity = [];

      if (sensitivityJson) {
        const sensitivity = JSON.parse(sensitivityJson);

        if (sensitivity.medication?.length) {
          sensitivity.medication.forEach(sens => {
            medication_sensitivity.push({
              reaction: sens.response,
              severity: sens.severity,
              name: sens.medication,
              notes: sens.notice
            });
          });
        }

        if (sensitivity.material?.length) {
          sensitivity.material.forEach(material => {
            material_sensitivity.push({
              reaction: material.response,
              severity: material.severity,
              name: material.material_name,
              notes: material.notice
            });
          });
        }
      }

      return { medication_sensitivity, material_sensitivity };
    },

    /**
     * Calculates insurance form values (payType, insurance_type, insuranceCompany)
     * Contains important business logic for RHINOPLASTY and insurance company detection
     */
    calculateInsuranceForm(surg, selected_surgery) {
      const insuranceOptions = insuranceQuestions.careServicesOptions;
      const insuranceCompanyOptions = this.insuranceQuestions
        .insuranceCompanyOptions;

      const tmpPayType = surg.insurance_name;
      const newInsurance = this.getShortCareServices(tmpPayType);
      const insuranceName = newInsurance;

      let payType = "";
      let insurance_type = insuranceName;
      let insuranceCompany = "";

      // Detect if it's an insurance company
      const isInsuranceCompanyFound = insuranceCompanyOptions.some(
        i =>
          i.value === insuranceName || i.value === surg.secondary_insurance_name
      );
      const isInsuranceCompany = isInsuranceCompanyFound ? "true" : "false";

      // Determine payType, insurance_type, and insuranceCompany
      const foundInsurance = insuranceOptions.find(
        option => option.value === insuranceName
      );

      if (foundInsurance) {
        insurance_type = foundInsurance.value;
        insuranceCompany = surg.secondary_insurance_name;
        payType = tmpPayType;
      } else {
        const foundInsuranceCompany = insuranceCompanyOptions.find(
          option => option.value === insuranceName
        );

        if (foundInsuranceCompany) {
          insurance_type = this.getShortCareServices(
            surg.secondary_insurance_name
          );
          insuranceCompany = foundInsuranceCompany.value;
          payType = "חברת ביטוח";
        } else {
          payType = insuranceName;
          if (surg.secondary_insurance_name) {
            const foundSecondaryInsurance = insuranceOptions.find(
              option =>
                option.value ===
                this.getShortCareServices(surg.secondary_insurance_name)
            );
            if (foundSecondaryInsurance) {
              insurance_type = foundSecondaryInsurance.value;
            } else {
              insurance_type = "ללא קופה";
            }
          }
        }
      }

      // RHINOPLASTY special case - important business logic
      if (
        selected_surgery.length === 1 &&
        selected_surgery[0].code_name_hospital === "1 > RHINOPLASTY" &&
        surg.secondary_insurance_name === "כללית מושלם"
      ) {
        payType = "כללית מושלם";
      }

      return {
        payType,
        insurance_type,
        insuranceCompany,
        isInsuranceCompany
      };
    },

    /**
     * Parses implants data from surgery response.
     * Backend returns implants as [{ id, supplier_name, name }] where `id` is
     * the generic_item_id. supplier_id is resolved later via populateImplantsSupplierIds
     * after the implants list (dropdown options) is fetched.
     */
    parseImplants(surgeryData) {
      const implantsData = (surgeryData.implants || []).map(implant => ({
        supplier_id: "",
        generic_item_id: implant.id ?? null,
        implantName: ""
      }));

      if (surgeryData.custom_implant) {
        implantsData.push({
          supplier_id: "other",
          generic_item_id: null,
          implantName: surgeryData.custom_implant
        });
      }

      return implantsData;
    },

    /**
     * Once implantsList is populated by getImplants, resolve supplier_id for each
     * implant by matching its generic_item_id against the dropdown options.
     */
    populateImplantsSupplierIds() {
      const implantsList = this.$store.state.Surgery.implantsList;
      const currentImplants =
        this.$store.state.Surgery.surgery.implantsData || [];
      if (!implantsList?.length || !currentImplants.length) return;

      const populated = currentImplants.map(item => {
        if (item.supplier_id === "other" || item.supplier_id) return item;
        const found = implantsList.find(
          i => i.generic_item_id == item.generic_item_id
        );
        return {
          ...item,
          supplier_id: found?.supplier_id || ""
        };
      });
      this.SET_SURGERY({ implantsData: populated });
    }
  },

  async mounted() {
    // Set edit mode and surgery ID in store
    this.SET_IS_EDIT_MODE(true);

    const surgeryId = this.$route.params.id;
    this.SET_SURGERY_ID(surgeryId);
    const [res, resOrders] = await Promise.all([
      this.$API.getSurgeryInfo(surgeryId),
      this.$API.getSurgeryOrders(surgeryId)
    ]);

    if (res?.status !== "OK") return;

    const surg = res.message.surgery;
    const patient = res.message.patient;

    // Load documents
    const resDoc = await getSurgeryDocuments(surgeryId);
    if (resDoc) {
      resDoc.forEach((doc, i) => {
        if (this.documents[i]) {
          this.documents[i].files = doc.files;
        }
      });
    }

    // Parse data
    const { selected_surgery, insurance_surgery } = this.parseSurgeryOrders(
      resOrders
    );
    const { current_diagnosis, past_diagnosis } = this.parseDiagnosis(
      surg.diagnosis
    );
    const {
      medication_sensitivity,
      material_sensitivity
    } = this.parseSensitivity(patient.sensitivity);
    const insuranceForm = this.calculateInsuranceForm(surg, selected_surgery);
    const implantsData = this.parseImplants(res.message.surgery);
    const patientRequests = parsePatientRequests(res, resOrders);

    // Format birth date
    let birth_date = "";
    if (patient.birthDate) {
      birth_date = this.$moment(patient.birthDate, "YYYY-MM-DD").format(
        "DD/MM/YYYY"
      );
    }

    // Update Surgery Store
    this.SET_SURGERY({
      id_num: patient.passport,
      passport_type: patient.passport_type || "",
      first_name: patient.firstName,
      last_name: patient.lastName,
      birth_date,
      parent_name: patient.apotropos || "",
      parental_status: res.message.surgery.parental_status || "",
      phone: patient.phone || "",
      phone2: patient.phone2 || "",
      lang: patient.language || "",
      email: patient.email || "",
      gender: this.parseGender(patient.gender),
      selected_surgery,
      initial_selected_surgery: JSON.parse(JSON.stringify(selected_surgery)),
      treatment_history: surg.indication,
      current_diagnosis,
      past_diagnosis,
      background_diagnosis: surg.checkbox_past_diagnosis ? "healthy" : "",
      phisycal_check: surg.physicalExam,
      no_drug_sens: !!patient.checkbox_medication_sensitivity,
      no_material_sens: !!patient.checkbox_material_sensitivity,
      medication_sensitivity,
      material_sensitivity,
      insurance_surgery,
      assistant: surg.assistant_status,
      assistant_name: surg.assistant_name,
      hospitalization: surg.hospitalization,
      anesthesia: surg.anesthesia,
      xray_type: surg.xray_type,
      select_frozen_cut: surg.frozen_cut,
      requests: {
        monitoring_request: surg.monitoring_request === "כן",
        blood: surg.blood === "כן",
        icu: surg.icu === "כן",
        tee_request: surg.tee_request === "כן",
        navigation_request: surg.navigation_request === "כן"
      },
      doctor_fee: surg.doctor_fee,
      insurance_type: insuranceForm.insurance_type,
      insuranceCompany: insuranceForm.insuranceCompany,
      isInsuranceCompany: insuranceForm.isInsuranceCompany,
      resources: surg.resources,
      notes: surg.notes,
      handle_insurance_claim_by_hmc: surg.handle_insurance_claim_by_hmc,
      files: this.documents,
      confirm_agreement: this.documents[0].files.length ? "true" : "false",
      payment_token: surg.payment_token,
      pre_surgery_override: surg.preSurgery_status === "pre_needed",
      implantsData,
      patient_requests: patientRequests,
      sent_patient_request_types: [...patientRequests.types]
    });

    // Set insurance form in store
    this.SET_INSURANCE_FORM({
      payType: insuranceForm.payType,
      isPayRequired: ""
    });

    // Set insurance surgery temp for insurance code selection
    this.SET_INSURANCE_SURGERY_TEMP(insurance_surgery);

    // Initialize isInsuranceType based on payType and fetch insurance codes
    await this.initInsuranceType(insuranceForm.payType, insurance_surgery);

    // Format initial_selected_surgery to match getFormattedOrders format
    // This is required for proper comparison in editSurgeryOrders
    await this.formatInitialSelectedSurgery();

    // Fetch implants for selected surgeries
    await this.getImplants();

    // Resolve supplier_id for each saved implant from the loaded options list
    this.populateImplantsSupplierIds();

    // Mark as ready to render SurgAdd
    this.isReady = true;
  }
};
</script>
